import type { CountryFeature } from "../types";

export type PathPoint = [number, number, number]; // [lat, lng, altitude]

export interface CountryPath {
  country: CountryFeature;
  key: string; // stable identifier for diffing
  points: PathPoint[];
  lifted?: boolean; // true = floating ghost copy above the base outline
  role?: "hover" | "select"; // which lift animation drives this ghost
}

/**
 * Convert every polygon ring of every country feature into a separate path.
 * Each point is a [lat, lng, altitude] triple — altitude is baked per-point
 * so three-globe's per-point `pathPointAlt` accessor can lift the whole
 * outline above the dot layer when the country is hovered or selected.
 */
export function countriesToPaths(
  features: CountryFeature[],
  altitude: (country: CountryFeature) => number
): CountryPath[] {
  const out: CountryPath[] = [];
  for (const f of features) {
    const geom = f.geometry;
    if (!geom) continue;
    const polys: number[][][][] =
      geom.type === "MultiPolygon"
        ? (geom.coordinates as number[][][][])
        : [geom.coordinates as number[][][]];
    const countryCode =
      f.properties.ISO_A3 ??
      f.properties.iso_a3 ??
      f.properties.ADMIN ??
      f.properties.admin ??
      "";
    let ringIdx = 0;
    for (let pi = 0; pi < polys.length; pi++) {
      const poly = polys[pi];
      for (let ri = 0; ri < poly.length; ri++) {
        const ring = poly[ri];
        if (!ring || ring.length < 2) continue;
        const alt = altitude(f);
        out.push({
          country: f,
          key: `${countryCode}:${pi}:${ri}:${ringIdx++}`,
          points: ring.map(
            ([lng, lat]) => [lat, lng, alt] as PathPoint
          ),
        });
      }
    }
  }
  return out;
}
